<template>
  <dashboard-layout title="System Log" back-to="/system-logs" back-label="System Logs">
    <loading-state v-if="loading" label="Loading log…" />

    <template v-else-if="log">
      <data-panel title="Overview">
        <div class="field-grid">
          <div class="field-row">
            <span class="field-label">Timestamp</span>
            <span class="field-value">{{ formatDate(log.createdAt) }}</span>
          </div>

          <div class="field-row">
            <span class="field-label">Level</span>
            <span class="field-value">
              <status-badge v-if="log.level" :variant="levelVariant" :label="log.level" />
              <span v-else>-</span>
            </span>
          </div>

          <div class="field-row">
            <span class="field-label">Function Name</span>
            <span class="field-value">{{ log.functionName || '-' }}</span>
          </div>

          <div class="field-row">
            <span class="field-label">Line Number</span>
            <span class="field-value">{{ log.lineNumber || '-' }}</span>
          </div>

          <div class="field-row">
            <span class="field-label">Message</span>
            <span class="field-value">{{ log.message || '-' }}</span>
          </div>

          <div class="field-row">
            <span class="field-label">User ID</span>
            <span
              v-if="log.userId"
              class="field-value user-id-link"
              @click="openUserDetail(log.userId)"
            >
              {{ log.userId }}
            </span>
            <span v-else class="field-value">-</span>
          </div>
        </div>
      </data-panel>

      <data-panel v-if="log.error" title="Error">
        <pre class="detail-code">{{ formatJSON(log.error) }}</pre>
      </data-panel>

      <data-panel v-if="log.metadata" title="Metadata">
        <pre class="detail-code">{{ formatJSON(log.metadata) }}</pre>
      </data-panel>
    </template>

    <empty-state v-else title="Log not found" icon="filetext">
      <router-link to="/system-logs" class="back-inline">Back to System Logs</router-link>
    </empty-state>
  </dashboard-layout>
</template>

<script>
import { ref, computed, onMounted } from 'vue'
import { useRoute } from 'vue-router'
import { FirebaseSystemLogService } from '../services/firebaseSystemLogService'
import DashboardLayout from './ui/DashboardLayout.vue'
import DataPanel from './ui/DataPanel.vue'
import StatusBadge from './ui/StatusBadge.vue'
import LoadingState from './ui/LoadingState.vue'
import EmptyState from './ui/EmptyState.vue'

const LEVEL_VARIANTS = {
  error: 'danger',
  warn: 'warning',
  info: 'info',
  success: 'success'
}

export default {
  name: 'SystemLogDetailView',
  components: {
    DashboardLayout, DataPanel, StatusBadge, LoadingState, EmptyState
  },
  setup() {
    const route = useRoute()
    const service = new FirebaseSystemLogService()
    const log = ref(null)
    const loading = ref(true)

    const load = async () => {
      loading.value = true
      try {
        const logId = route.params.id
        log.value = await service.getLogById(logId)
      } catch (error) {
        console.error('Error loading log:', error)
      } finally {
        loading.value = false
      }
    }

    const levelVariant = computed(() => LEVEL_VARIANTS[log.value?.level] || 'neutral')

    const formatDate = (val) => {
      if (!val) return '-'
      const date = val.seconds ? new Date(val.seconds * 1000) : new Date(val)
      return date.toLocaleString('tr-TR')
    }

    const formatJSON = (val) => {
      if (!val) return '-'
      if (typeof val === 'object') {
        return JSON.stringify(val, null, 2)
      }
      return val
    }

    const openUserDetail = (userId) => {
      if (!userId) return
      window.open(`/user/${userId}`, '_blank')
    }

    onMounted(load)

    return {
      log,
      loading,
      levelVariant,
      formatDate,
      formatJSON,
      openUserDetail
    }
  }
}
</script>

<style scoped>
.field-grid {
  display: flex;
  flex-direction: column;
  gap: 16px;
}
.field-row {
  display: flex;
  gap: 16px;
  align-items: flex-start;
}
.field-label {
  font-weight: 600;
  color: var(--text-secondary);
  min-width: 150px;
  font-size: 12px;
  text-transform: uppercase;
  letter-spacing: 0.5px;
}
.field-value {
  color: var(--text-primary);
  font-size: 14px;
  word-break: break-word;
  flex: 1;
}
.user-id-link {
  color: var(--primary-color);
  cursor: pointer;
  text-decoration: underline;
}
.user-id-link:hover {
  color: var(--gradient-2);
}
.detail-code {
  margin: 0;
  background: var(--field-bg);
  padding: 16px;
  border-radius: 8px;
  border: 1px solid var(--border-soft);
  white-space: pre-wrap;
  word-break: break-all;
  font-size: 13px;
  line-height: 1.5;
  color: var(--text-primary);
  overflow-x: auto;
  font-family: 'Courier New', monospace;
}
.back-inline {
  color: var(--primary-color);
  text-decoration: none;
}
.back-inline:hover {
  text-decoration: underline;
}

@media (max-width: 640px) {
  .field-row { flex-direction: column; gap: 4px; }
}
</style>
